import type { Prisma, PrismaClient, ReminderRule, SalesDocument } from '@prisma/client';
import Decimal from 'decimal.js';
import { formatMoney, whatsappLink } from '@roovers/shared';
import { runDueRecurring, scheduleMissingRenewalAlerts } from '../automation/recurring.service.js';
import { scheduleMissingLeadFollowUps } from '../crm/crm.service.js';
import { cancelJobs, enqueueJob, registerJobHandler } from '../../core/jobs.js';
import { queueEmail, renderTemplate } from '../messaging/messaging.service.js';
import { outstanding } from './calc.js';
import { addDays, isoOf, logEvent } from './documents.service.js';
import { createShareLink, shareUrl } from './share.service.js';

type Db = PrismaClient | Prisma.TransactionClient;

export interface ReminderSettings {
  /** off = no client-facing reminders, ask = create a task only, auto = email the client. */
  clientMode: 'off' | 'ask' | 'auto';
  windowStartHour: number;
  windowEndHour: number;
  maxClientReminders: number;
}
export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = {
  clientMode: 'auto',
  windowStartHour: 8,
  windowEndHour: 18,
  maxClientReminders: 2,
};

export async function getReminderSettings(db: Db, companyId: number): Promise<ReminderSettings> {
  const row = await db.setting.findUnique({
    where: { companyId_key: { companyId, key: 'reminders' } },
  });
  return {
    ...DEFAULT_REMINDER_SETTINGS,
    ...((row?.value as Partial<ReminderSettings> | undefined) ?? {}),
  };
}

/**
 * Default rules (PLAN.md 7.9.2). Quotations: an internal + client follow-up 3 days after sending, a final
 * client nudge at 7 days, and an owner warning 3 days before expiry. Invoices: before the due date, on
 * it, and 3 / 7 / 14 days after.
 */
export const DEFAULT_RULES: Omit<ReminderRule, 'id' | 'companyId'>[] = [
  {
    docType: 'quotation',
    name: 'Follow up 3 days after sending',
    trigger: 'after_sent',
    offsetDays: 3,
    audience: 'both',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'quotation',
    name: 'Final nudge 7 days after sending',
    trigger: 'after_sent',
    offsetDays: 7,
    audience: 'client',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'quotation',
    name: 'Expiry warning',
    trigger: 'before_expiry',
    offsetDays: 3,
    audience: 'owner',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'invoice',
    name: 'Due in 3 days',
    trigger: 'before_due',
    offsetDays: 3,
    audience: 'client',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'invoice',
    name: 'Due today',
    trigger: 'after_due',
    offsetDays: 0,
    audience: 'client',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'invoice',
    name: '3 days overdue',
    trigger: 'after_due',
    offsetDays: 3,
    audience: 'both',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'invoice',
    name: '7 days overdue',
    trigger: 'after_due',
    offsetDays: 7,
    audience: 'both',
    templateName: 'reminder',
    active: true,
  },
  {
    docType: 'invoice',
    name: '14 days overdue',
    trigger: 'after_due',
    offsetDays: 14,
    audience: 'both',
    templateName: 'reminder',
    active: true,
  },
];

export async function ensureReminderDefaults(db: PrismaClient, companyId: number): Promise<void> {
  if ((await db.reminderRule.count({ where: { companyId } })) > 0) return;
  await db.reminderRule.createMany({ data: DEFAULT_RULES.map((r) => ({ ...r, companyId })) });
}

// ───────────── time helpers (Africa/Kampala is UTC+3, no daylight saving) ─────────────

const EAT_OFFSET_H = 3;
export const localHour = (d: Date) => (d.getUTCHours() + EAT_OFFSET_H) % 24;

/** The next moment inside the send window (default 08:00 to 18:00 Kampala time). */
export function nextSendTime(now: Date, startHour: number, endHour: number): Date {
  const h = localHour(now);
  if (h >= startHour && h < endHour) return now;
  const next = new Date(now);
  next.setUTCMinutes(0, 0, 0);
  const startUtc = startHour - EAT_OFFSET_H;
  next.setUTCHours(startUtc);
  if (next <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next;
}

/** 09:00 Kampala on a given calendar date. */
const at9 = (date: Date) =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 9 - EAT_OFFSET_H, 0, 0),
  );

export function reminderRunAt(
  rule: Pick<ReminderRule, 'trigger' | 'offsetDays'>,
  doc: Pick<SalesDocument, 'sentAt' | 'dueDate' | 'validUntil'>,
): Date | null {
  switch (rule.trigger) {
    case 'after_sent':
      return doc.sentAt ? new Date(doc.sentAt.getTime() + rule.offsetDays * 86_400_000) : null;
    case 'before_expiry':
      return doc.validUntil ? at9(addDays(doc.validUntil, -rule.offsetDays)) : null;
    case 'before_due':
      return doc.dueDate ? at9(addDays(doc.dueDate, -rule.offsetDays)) : null;
    case 'after_due':
      return doc.dueDate ? at9(addDays(doc.dueDate, rule.offsetDays)) : null;
    default:
      return null;
  }
}

const jobKey = (docType: string, docId: number, ruleId: number) =>
  `rem:${docType}:${docId}:${ruleId}`;

/** (Re)schedule every active rule for a document. Safe to call again: existing pending jobs are kept. */
export async function scheduleDocumentReminders(
  db: PrismaClient,
  doc: SalesDocument,
  now = new Date(),
): Promise<number> {
  if (doc.remindersStopped || !['quotation', 'invoice'].includes(doc.docType)) return 0;
  const rules = await db.reminderRule.findMany({
    where: { companyId: doc.companyId, docType: doc.docType, active: true },
  });
  let scheduled = 0;
  for (const rule of rules) {
    const runAt = reminderRunAt(rule, doc);
    if (!runAt) continue;
    if (runAt.getTime() < now.getTime() - 86_400_000) continue; // long past: do not burst old reminders
    const existing = await db.scheduledReminder.findUnique({
      where: { docId_ruleId: { docId: doc.id, ruleId: rule.id } },
    });
    if (existing && existing.status !== 'cancelled') continue;
    const row = existing
      ? await db.scheduledReminder.update({
          where: { id: existing.id },
          data: { status: 'pending', runAt, firedAt: null },
        })
      : await db.scheduledReminder.create({
          data: {
            companyId: doc.companyId,
            ruleId: rule.id,
            docType: doc.docType,
            docId: doc.id,
            runAt,
          },
        });
    await db.job.deleteMany({
      where: { idempotencyKey: jobKey(doc.docType, doc.id, rule.id), status: 'cancelled' },
    });
    await enqueueJob(db, {
      companyId: doc.companyId,
      type: 'reminder.fire',
      payload: { scheduledId: row.id },
      runAt,
      idempotencyKey: jobKey(doc.docType, doc.id, rule.id),
    });
    scheduled += 1;
  }
  return scheduled;
}

export async function cancelDocumentReminders(
  db: Db,
  docType: string,
  docId: number,
): Promise<void> {
  await cancelJobs(db, `rem:${docType}:${docId}:`);
  await db.scheduledReminder.updateMany({
    where: { docType, docId, status: 'pending' },
    data: { status: 'cancelled' },
  });
}

export async function snoozeDocumentReminders(
  db: PrismaClient,
  docType: string,
  docId: number,
  days: number,
): Promise<number> {
  const pending = await db.scheduledReminder.findMany({
    where: { docType, docId, status: 'pending' },
  });
  for (const r of pending) {
    const runAt = new Date(Math.max(r.runAt.getTime(), Date.now()) + days * 86_400_000);
    await db.scheduledReminder.update({ where: { id: r.id }, data: { runAt } });
    await db.job.updateMany({
      where: { idempotencyKey: jobKey(docType, docId, r.ruleId), status: 'pending' },
      data: { runAt },
    });
  }
  return pending.length;
}

// ───────────── firing ─────────────

export interface ReminderContext {
  db: PrismaClient;
  config: { APP_URL: string };
  now: Date;
}

function stillApplicable(doc: SalesDocument): string | null {
  if (doc.remindersStopped) return 'reminders stopped for this document';
  if (doc.docType === 'quotation') {
    if (!['sent', 'viewed'].includes(doc.status)) return `quotation is ${doc.status}`;
    if (doc.respondedAt) return 'the client has responded';
  } else if (doc.docType === 'invoice') {
    if (!['approved', 'sent', 'partially_paid'].includes(doc.status))
      return `invoice is ${doc.status}`;
    if (outstanding(doc).lte(0)) return 'nothing is outstanding';
  } else return 'unsupported document type';
  return null;
}

/** Runs one scheduled reminder. Conditions are re-checked here, not when it was scheduled. */
export async function fireReminder(
  ctx: ReminderContext,
  scheduledId: number,
): Promise<{ rescheduleAt: Date } | void> {
  const { db, now } = ctx;
  const sched = await db.scheduledReminder.findUnique({ where: { id: scheduledId } });
  if (!sched || sched.status !== 'pending') return;
  const rule = await db.reminderRule.findUnique({ where: { id: sched.ruleId } });
  const doc = await db.salesDocument.findUnique({ where: { id: sched.docId } });
  const skip = async (note: string) => {
    await db.scheduledReminder.update({
      where: { id: sched.id },
      data: { status: 'skipped', note: note.slice(0, 300), firedAt: now },
    });
  };
  if (!rule || !rule.active) return skip('rule disabled');
  if (!doc) return skip('document not found');
  const reason = stillApplicable(doc);
  if (reason) return skip(reason);

  const settings = await getReminderSettings(db, doc.companyId);
  const party = await db.party.findUnique({
    where: { id: doc.partyId },
    include: { contacts: true },
  });
  const company = await db.company.findUniqueOrThrow({ where: { id: doc.companyId } });
  const kindLabel = doc.docType === 'quotation' ? 'Quotation' : 'Invoice';
  const total = formatMoney(doc.total.toString(), doc.currency);
  const vars = {
    client_name: party?.name ?? '',
    document_number: doc.number ?? '',
    total,
    due_date: isoOf(doc.dueDate) ?? '',
    valid_until: isoOf(doc.validUntil) ?? '',
    company_name: company.name,
    sender_name: company.name,
  };
  const owner = doc.ownerId ? await db.user.findUnique({ where: { id: doc.ownerId } }) : null;
  const wantsOwner = rule.audience === 'owner' || rule.audience === 'both';
  const wantsClient = rule.audience === 'client' || rule.audience === 'both';

  // Client-facing messages only go out inside the send window.
  const willEmailClient = wantsClient && settings.clientMode === 'auto';
  if (willEmailClient) {
    const when = nextSendTime(now, settings.windowStartHour, settings.windowEndHour);
    if (when.getTime() > now.getTime()) return { rescheduleAt: when };
  }

  const link = shareUrl(
    ctx.config.APP_URL,
    (
      await createShareLink(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: doc.id,
      })
    ).token,
  );
  const contact =
    party?.contacts.find((c) => c.receivesDocuments && c.email) ??
    party?.contacts.find((c) => c.email);
  const waTarget = party?.contacts.find((c) => c.whatsappNumber || c.phone);
  const template = await db.messageTemplate.findFirst({
    where: {
      companyId: doc.companyId,
      docType: doc.docType,
      channel: 'email',
      name: rule.templateName,
    },
  });
  const waTemplate = await db.messageTemplate.findFirst({
    where: {
      companyId: doc.companyId,
      docType: doc.docType,
      channel: 'whatsapp',
      name: rule.templateName,
    },
  });
  const emailBody = renderTemplate(
    template?.body ??
      `Dear {{client_name}},\n\nA reminder about ${kindLabel.toLowerCase()} {{document_number}} ({{total}}): {{link}}`,
    { ...vars, link },
  );
  const emailSubject = renderTemplate(
    template?.subject ?? `Reminder: ${kindLabel.toLowerCase()} {{document_number}}`,
    { ...vars, link },
  );
  const waText = renderTemplate(
    waTemplate?.body ??
      `Hello {{client_name}}, a quick reminder about ${kindLabel.toLowerCase()} {{document_number}}: {{link}}`,
    { ...vars, link },
  );
  const waUrl = waTarget
    ? whatsappLink(waTarget.whatsappNumber ?? waTarget.phone ?? '', waText)
    : null;

  const ageDays = doc.sentAt
    ? Math.max(0, Math.floor((now.getTime() - doc.sentAt.getTime()) / 86_400_000))
    : 0;
  const headline =
    doc.docType === 'quotation'
      ? `No response to ${doc.number} (${party?.name}, ${total}), sent ${ageDays} day${ageDays === 1 ? '' : 's'} ago`
      : `Invoice ${doc.number} (${party?.name}, ${total}) ${doc.dueDate && doc.dueDate < now ? 'is overdue' : 'is due soon'}`;

  if (wantsOwner && owner) {
    const dueDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    await db.task.create({
      data: {
        companyId: doc.companyId,
        assigneeId: owner.id,
        title: headline,
        description: [
          waUrl ? `Open WhatsApp reminder: ${waUrl}` : null,
          contact?.email ? `Client email: ${contact.email}` : 'No email on file for this client.',
          `Document: ${link}`,
        ]
          .filter(Boolean)
          .join('\n'),
        dueDate,
        relatedType: 'sales_document',
        relatedId: doc.id,
        source: 'reminder',
      },
    });
    await db.notification.create({
      data: {
        companyId: doc.companyId,
        userId: owner.id,
        title: headline,
        body: 'Resend, open the WhatsApp reminder, call, mark responded, snooze or stop reminders.',
        link: `/sales/${doc.docType === 'quotation' ? 'quotations' : 'invoices'}/${doc.id}`,
      },
    });
    await queueEmail(db as PrismaClient, {
      companyId: doc.companyId,
      to: [owner.email],
      subject: headline,
      text: `${headline}\n\nOpen it: ${ctx.config.APP_URL}/sales/${doc.docType === 'quotation' ? 'quotations' : 'invoices'}/${doc.id}${waUrl ? `\nWhatsApp reminder: ${waUrl}` : ''}`,
      docType: doc.docType,
      docId: doc.id,
      templateName: 'reminder_internal',
    });
  }

  let note = 'internal reminder created';
  if (wantsClient) {
    if (settings.clientMode === 'off') note = 'client reminders are switched off';
    else if (settings.clientMode === 'ask')
      note = 'task created; waiting for you to send the client reminder';
    else {
      const sentCount = await db.communication.count({
        where: {
          companyId: doc.companyId,
          docType: doc.docType,
          docId: doc.id,
          templateName: 'reminder',
          status: { in: ['queued', 'sent'] },
        },
      });
      if (sentCount >= settings.maxClientReminders)
        note = `client reminder limit (${settings.maxClientReminders}) reached`;
      else if (!contact?.email) note = 'client has no email on file';
      else {
        await queueEmail(db as PrismaClient, {
          companyId: doc.companyId,
          to: [contact.email],
          subject: emailSubject,
          text: emailBody,
          docType: doc.docType,
          docId: doc.id,
          templateName: 'reminder',
        });
        note = `client reminder emailed to ${contact.email}`;
      }
    }
  }
  await db.scheduledReminder.update({
    where: { id: sched.id },
    data: { status: 'fired', firedAt: now, note: note.slice(0, 300) },
  });
  await logEvent(db, {
    companyId: doc.companyId,
    entityType: 'sales_document',
    entityId: doc.id,
    type: 'reminder',
    note: `${rule.name}: ${note}`,
  });
}

export function registerReminderJobs() {
  registerJobHandler('reminder.fire', async (job, ctx) => {
    const { scheduledId } = job.payload as { scheduledId: number };
    return fireReminder(
      { db: ctx.db, config: { APP_URL: ctx.config.APP_URL }, now: ctx.now },
      scheduledId,
    );
  });
}

/** Daily housekeeping: quotations past their validity date expire and stop nagging. */
export async function runSalesSweeps(
  db: PrismaClient,
  now = new Date(),
  config: { APP_URL: string } = { APP_URL: 'http://localhost:5173' },
): Promise<{ expired: number; leadFollowUps: number; recurring: number; renewalAlerts: number }> {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const due = await db.salesDocument.findMany({
    where: {
      docType: 'quotation',
      status: { in: ['approved', 'sent', 'viewed'] },
      validUntil: { lt: today },
    },
  });
  for (const q of due) {
    await db.salesDocument.update({ where: { id: q.id }, data: { status: 'expired' } });
    await cancelDocumentReminders(db, 'quotation', q.id);
    await logEvent(db, {
      companyId: q.companyId,
      entityType: 'sales_document',
      entityId: q.id,
      type: 'expired',
    });
  }
  const leadFollowUps = await scheduleMissingLeadFollowUps(db);
  const recurring = (await runDueRecurring(db, config, now)).filter(
    (r) => r.status === 'created',
  ).length;
  const renewalAlerts = await scheduleMissingRenewalAlerts(db);
  return { expired: due.length, leadFollowUps, recurring, renewalAlerts };
}

export const _internal = { jobKey, Decimal };
